/**
 * BlackPantherShockwave.js
 * Implements Black Panther's thorns ability and shockwave
 */

import { Easing } from "@tweenjs/tween.js";
import { Game, GameAnimatedSprite, GameScene, GameSprite, GameTimer, GameTween } from "pixi_core";
import { GameEvents, SceneLayers } from "../Constants";
import { Common } from "./Common";

export class BlackPantherShockwave {
    /**
     * @public
     * @param {GameScene} scene
     */
    constructor(scene) {
        /** @private @type {GameScene} */
        this._scene = scene;

        /** @private @type {GameTween} */
        this._tweenShockwave = null;

        /** @private @type {GameTimer} */
        this._timerDelayHideFlash = null;

        /** @private @type {GameTimer} */
        this._timerDelayShowFlash = null;

        /** @private @type {Number} */
        this._flashCounter = 0;

        Game.on(GameEvents.eHERO_ABILITY_BLACK_PANTHER_ACTIVATE, this._onSpawnShield, this);
        Game.on(GameEvents.eHERO_ABILITY_BLACK_PANTHER_ATTACK, this._onShockwave, this);
        Game.on(GameEvents.eHEROES_HIT, this._onFlashShield, this);

        this._initialize();
    }

    /**
     * @private
     */
    _initialize() {
        /** @private @type {GameAnimatedSprite} */
        this._blackPantherShield = new GameAnimatedSprite(
            null,
            Game.design.getGraphicData("BlackPantherShield"),
            this._scene,
            SceneLayers.eWALLS
        );
        this._blackPantherShield.visible = false;
        this._blackPantherShield.x = Common.settings.blackPantherShieldPosX;
        this._blackPantherShield.y = 0;

        /** @private @type {GameSprite} */
        this._blackPantherShockwave = new GameSprite(Game.design.getGraphicData("BlackPantherShockwave"));
        this._scene.setObjectLayer(this._blackPantherShockwave, SceneLayers.eOVER_BUBBLES_UI);
        this._blackPantherShockwave.visible = false;

        /** @private @type {GameSprite} */
        this._blackPantherFeedbackWhite = new GameSprite(Game.design.getGraphicData("BlackPantherFeedbackWhite"));
        this._scene.setObjectLayer(this._blackPantherFeedbackWhite, SceneLayers.eOVER_BUBBLES_UI);
        this._blackPantherFeedbackWhite.visible = false;
        this._blackPantherFeedbackWhite.x = Common.settings.blackPantherShieldPosX;
        this._blackPantherFeedbackWhite.y = 0;
    }

    /**
     * @private
     */
    _onFlashShield() {
        if (!this._blackPantherShield.visible) return;

        Game.audio.playSfx("IDSND_ABIL_BP_SHEILD_PULSE");
        this._flashCounter = Common.settings.attackPhaseFlashCount;
        this._blackPantherFeedbackWhite.visible = true;

        if (this._timerDelayShowFlash) {
            this._timerDelayShowFlash.destroy();
            this._timerDelayShowFlash = null;
        }

        if (this._timerDelayHideFlash) {
            this._timerDelayHideFlash.destroy();
            this._timerDelayHideFlash = null;
        }

        this._timerDelayShowFlash = Game.windows
            .getCurrentWindow()
            .createTimer(Common.settings.attackPhaseFlashShowDelay, { autoStart: false, deleteOnCompletion: false });
        this._timerDelayShowFlash.onComplete = () => {
            this._blackPantherFeedbackWhite.visible = true;
            this._timerDelayHideFlash.start();
        };

        this._timerDelayHideFlash = Game.windows
            .getCurrentWindow()
            .createTimer(Common.settings.attackPhaseFlashHideDelay, { deleteOnCompletion: false });
        this._timerDelayHideFlash.onComplete = () => {
            this._blackPantherFeedbackWhite.visible = false;

            --this._flashCounter;
            if (this._flashCounter > 0) {
                this._timerDelayShowFlash.start();
            } else {
                this._onFlashCompleted();
            }
        };
    }

    /**
     * @private
     */
    _onFlashCompleted() {
        this._destroyTweens();
    }

    /**
     * @private
     */
    _onSpawnShield() {
        this._feedbackCount = 0;
        this._blackPantherShield.visible = true;
        this._blackPantherShield.playAnimation("spawn");
        this._blackPantherShield.onComplete = () => {
            this._blackPantherShield.playAnimation("idle");
        };
        Game.audio.playSfx("IDSND_ABIL_BP_SHEILD_ACTIVATE");
    }

    /**
     * @private
     * @param {Boolean} stillActive
     * @param {Number} nbBubbles
     */
    _onShockwave(stillActive, nbBubbles) {
        Game.audio.playSfx("IDSND_ABIL_BP_SHEILD_PULSE");

        this._shockwaveActive = stillActive;
        this._blackPantherShield.visible = stillActive;

        if (!stillActive) {
            this._blackPantherShield.stop();
        }

        this._blackPantherShockwave.visible = true;
        this._blackPantherShockwave.alpha = 1;
        this._blackPantherShockwave.x = this._blackPantherShield.x;
        this._blackPantherShockwave.y = this._blackPantherShield.y;

        if (this._tweenShockwave) {
            this._tweenShockwave.destroy();
            this._tweenShockwave = null;
        }

        this._tweenShockwave = Game.windows
            .getCurrentWindow()
            .createTween(this._blackPantherShockwave)
            .to({ x: Common.settings.blackPantherShockwavePosX, alpha: 0.1 }, Common.settings.blackPantherShockwaveTime)
            .easing(Easing.Cubic.Out)
            .onComplete(() => {
                this._blackPantherShockwave.visible = false;
            })
            .start();
    }

    _destroyTweens() {
        if (this._timerDelayShowFlash) {
            this._timerDelayShowFlash.destroy();
            this._timerDelayShowFlash = null;
        }

        if (this._timerDelayHideFlash) {
            this._timerDelayHideFlash.destroy();
            this._timerDelayHideFlash = null;
        }
    }

    /**
     * @public
     */
    destroy() {
        if (this._tweenShockwave) {
            this._tweenShockwave.destroy();
            this._tweenShockwave = null;
        }

        if (this._blackPantherShield) {
            this._blackPantherShield.destroy();
            this._blackPantherShield = null;
        }

        if (this._blackPantherShockwave) {
            this._blackPantherShockwave.destroy();
            this._blackPantherShockwave = null;
        }

        if (this._blackPantherFeedbackWhite) {
            this._blackPantherFeedbackWhite.destroy();
            this._blackPantherFeedbackWhite = null;
        }

        this._destroyTweens();

        Game.off(GameEvents.eHERO_ABILITY_BLACK_PANTHER_ACTIVATE, this._onSpawnShield, this);
        Game.off(GameEvents.eHERO_ABILITY_BLACK_PANTHER_ATTACK, this._onShockwave, this);
        Game.off(GameEvents.eHEROES_HIT, this._onFlashShield, this);
    }
}
